import type {
  CloseEventRequest,
  CreateEventRequest,
  CreateEventTemplateRequest,
  EventCloseReportResponse,
  EventResponse,
  EventStatus,
  EventTemplateResponse,
  ListActiveEventsResponse,
  ListEventsResponse,
  ListEventTemplatesResponse,
  UpdateEventRequest,
} from '@jq4race/shared';

import { request } from './client.js';

// ── Events (S1: create/edit/list/get + confirm/activate transitions) ───────

export async function listEvents(params?: { status?: EventStatus }): Promise<ListEventsResponse> {
  const qs = params?.status ? `?status=${encodeURIComponent(params.status)}` : '';
  return request<ListEventsResponse>(`/admin/events${qs}`, { method: 'GET' });
}

export async function createEvent(input: CreateEventRequest): Promise<EventResponse> {
  return request<EventResponse>('/admin/events', { method: 'POST', body: input });
}

export async function getEvent(id: string): Promise<EventResponse> {
  return request<EventResponse>(`/admin/events/${encodeURIComponent(id)}`, { method: 'GET' });
}

export async function updateEvent(id: string, input: UpdateEventRequest): Promise<EventResponse> {
  return request<EventResponse>(`/admin/events/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
  });
}

/** draft → scheduled (R1.4) */
export async function confirmEvent(id: string): Promise<EventResponse> {
  return request<EventResponse>(`/admin/events/${encodeURIComponent(id)}/confirm`, {
    method: 'POST',
  });
}

/** scheduled → active (R1.4) */
export async function activateEvent(id: string): Promise<EventResponse> {
  return request<EventResponse>(`/admin/events/${encodeURIComponent(id)}/activate`, {
    method: 'POST',
  });
}

/** active → closed (S6, R6.1/R6.3/R6.4) */
export async function closeEvent(
  id: string,
  input: CloseEventRequest = {},
): Promise<EventResponse> {
  return request<EventResponse>(`/admin/events/${encodeURIComponent(id)}/close`, {
    method: 'POST',
    body: input,
  });
}

/** S6, R6.2/R6.5 — read-only, only available once the event is closed. */
export async function getEventReport(id: string): Promise<EventCloseReportResponse> {
  return request<EventCloseReportResponse>(`/admin/events/${encodeURIComponent(id)}/report`, {
    method: 'GET',
  });
}

/**
 * Operator-scoped GET /events/active (S4-UI prerequisite) — how the Flash
 * check-in screen lets an operator discover which event is currently active
 * before calling batch-checkin or guest-match, both of which need an
 * eventId. Deliberately a minimal summary, not the full admin EventResponse.
 */
export async function listActiveEvents(): Promise<ListActiveEventsResponse> {
  return request<ListActiveEventsResponse>('/events/active', { method: 'GET' });
}

// ── Event templates (R1.7/R1.8) ─────────────────────────────────────────────

export async function listEventTemplates(): Promise<ListEventTemplatesResponse> {
  return request<ListEventTemplatesResponse>('/admin/event-templates', { method: 'GET' });
}

export async function createEventTemplate(
  input: CreateEventTemplateRequest,
): Promise<EventTemplateResponse> {
  return request<EventTemplateResponse>('/admin/event-templates', {
    method: 'POST',
    body: input,
  });
}
